import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, InfoIcon } from 'lucide-react';
import { useStore } from '../contexts/StoreContext';
import { OfferStatus } from '../components/OfferStatus';
import { AmortizationTable } from '../components/simulator/AmortizationTable';
import { CircularSimulator } from '../components/simulator/CircularSimulator';
import { BrandCirculars } from '../components/simulator/BrandCirculars';
import { InquiryForm } from '../components/InquiryForm';
import { Modal } from '../components/ui/Modal';
import { amortizationLabel, getDownBounds, getEffectiveOffer, isOfferPublishable, simulate } from '../utils/finance';
import { formatARS, formatDate, formatPct } from '../utils/format';
export function Simulator() {
  const {
    vehicles,
    brands
  } = useStore();
  const [params, setParams] = useSearchParams();
  const published = useMemo(() => vehicles.filter(v => v.published), [vehicles]);
  const initial = published.find(v => v.id === params.get('v'));
  const [brandId, setBrandId] = useState(initial?.brandId ?? '');
  const [model, setModel] = useState(initial?.model ?? '');
  const vehicleId = params.get('v') ?? '';
  const vehicle = published.find(v => v.id === vehicleId) ?? null;
  const ctx = vehicle ? getEffectiveOffer(vehicle, brands) : null;
  const bounds = vehicle && ctx ? getDownBounds(vehicle.price, ctx.offer) : null;
  const [down, setDown] = useState(0);
  const [downInput, setDownInput] = useState('');
  const [term, setTerm] = useState(0);
  const [showTable, setShowTable] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const models = useMemo(() => Array.from(new Set(published.filter(v => v.brandId === brandId).map(v => v.model))).sort(), [published, brandId]);
  const versions = useMemo(() => published.filter(v => v.brandId === brandId && v.model === model), [published, brandId, model]);

  // Al cambiar de unidad, se carga automáticamente su configuración financiera.
  const offerKey = ctx ? `${vehicle?.id}-${ctx.offer.updatedAt}-${vehicle?.price}` : '';
  useEffect(() => {
    if (!vehicle || !ctx || !bounds) return;
    setDown(bounds.min);
    setDownInput(String(bounds.min));
    setTerm(ctx.offer.terms.length ? Math.max(...ctx.offer.terms) : 0);
    setShowTable(false);
  }, [offerKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const sim = vehicle && ctx && term ? simulate(vehicle.price, down, term, ctx.offer) : null;
  const selectVehicle = (id: string) => {
    const p = new URLSearchParams(params);
    if (id) p.set('v', id);else p.delete('v');
    setParams(p, {
      replace: true
    });
  };
  const commitDown = (raw: string) => {
    if (!bounds) return;
    const n = Number(raw.replace(/\D/g, '')) || 0;
    const clamped = Math.min(bounds.max, Math.max(bounds.min, n));
    setDown(clamped);
    setDownInput(String(clamped));
  };
  const header = <>
      <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl" data-id="element-1315" data-mp="pt9oiza825mx">Simulador de financiación</h1>
      <p className="mt-2 max-w-2xl text-mist" data-id="element-1316" data-mp="ur1psjwrb43s">
        Elegí marca, modelo y versión: cargamos automáticamente la tasa, los plazos y el anticipo mínimo configurados para esa unidad.
      </p>
    </>;
  const selectors = <div className="card grid gap-3 p-5 sm:grid-cols-3" data-id="element-1317" data-mp="18qkm8fg7gr09">
            <div data-id="element-1318" data-mp="18qavuqr90hu1">
              <label htmlFor="sim-brand" className="label" data-id="element-1319" data-mp="18oqbtv2k8haf">Marca</label>
              <select id="sim-brand" className="input" value={brandId} onChange={e => {
        setBrandId(e.target.value);
        setModel('');
        selectVehicle('');
      }} data-id="element-1320" data-mp="1do2zgi199se2">
                <option value="" data-id="element-1321" data-mp="1nfyxxu797kit">Elegí una marca</option>
                {brands.map(b => <option key={b.id} value={b.id} data-id="element-1322" data-mp="1nha420o694d3">{b.name}</option>)}
              </select>
            </div>
            <div data-id="element-1323" data-mp="1wg5939fhc8et">
              <label htmlFor="sim-model" className="label" data-id="element-1324" data-mp="1wfm0wkfmsydz">Modelo</label>
              <select id="sim-model" className="input" value={model} disabled={!brandId} onChange={e => {
        setModel(e.target.value);
        selectVehicle('');
      }} data-id="element-1325" data-mp="1itevahtk6b6f">
                <option value="" data-id="element-1326" data-mp="1er1mtlnffz5y">Elegí un modelo</option>
                {models.map(m => <option key={m} value={m} data-id="element-1327" data-mp="1sjhmcch4df24">{m}</option>)}
              </select>
            </div>
            <div data-id="element-1328" data-mp="1njv7jyln7qr7">
              <label htmlFor="sim-version" className="label" data-id="element-1329" data-mp="1nlg41tripjrp">Versión</label>
              <select id="sim-version" className="input" value={vehicleId} disabled={!model} onChange={e => selectVehicle(e.target.value)} data-id="element-1330" data-mp="zu0zduc9qqda">
                <option value="" data-id="element-1331" data-mp="vus42l1kykfm">Elegí una versión</option>
                {versions.map(v => <option key={v.id} value={v.id} data-id="element-1332" data-mp="19tjd3t1l5fww">{v.version}</option>)}
              </select>
            </div>
          </div>;
  if (!vehicle) {
    return <div className="container-x py-8 lg:py-12" data-id="element-1333" data-mp="2l7n6ax0cn00w">
        {header}
        <div className="mt-8" data-id="element-1334" data-mp="2l9zye2ztmqro">{selectors}</div>
        <div className="mt-6" data-id="element-1335" data-mp="2g9c0qyh37boh">
          <BrandCirculars brands={brands} brandId={brandId} onBrandChange={id => {
          setBrandId(id);
          setModel('');
        }} data-id="element-1336" data-mp="2g8akmfl7ryzw" />
        </div>
      </div>;
  }
  if (ctx?.circular) {
    return <div className="container-x py-8 lg:py-12" data-id="element-1337" data-mp="3p5qwancx52ub">
        {header}
        <div className="mt-8" data-id="element-1338" data-mp="3p7lie4zxf0zz">{selectors}</div>
        <div className="mt-6" data-id="element-1339" data-mp="3u4ke2rz8ng0a">
          <CircularSimulator vehicle={vehicle} circular={ctx.circular.circular} data-id="element-1340" data-mp="3u5n12otfwyax" />
        </div>
      </div>;
  }
  return <div className="container-x py-8 lg:py-12" data-id="element-1341" data-mp="lptsqx99it4y">
      {header}

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_420px]" data-id="element-1342" data-mp="ppkj4zuvn78y">
        <div className="space-y-6" data-id="element-1343" data-mp="bzh16l315zpb">
          {selectors}

          {!vehicle || !ctx || !bounds ? <div className="rounded-2xl border border-dashed border-line p-10 text-center text-sm text-mist" data-id="element-1344" data-mp="gy208h6jqdc6">
              Seleccioná una versión para cargar sus condiciones financieras.
            </div> : <>
              <div className="card flex items-center gap-4 p-4" data-id="element-1345" data-mp="19fed9kl2dygc">
                <img src={vehicle.images[0]} alt="" className="h-16 w-24 rounded-lg object-cover" data-id="element-1346" data-mp="1jb4tqf2y0zfs" />
                <div className="min-w-0 flex-1" data-id="element-1347" data-mp="1eaz1oxsrhax6">
                  <p className="truncate font-semibold" data-id="element-1348" data-mp="1eca00i3ftqgw">{brands.find(b => b.id === vehicle.brandId)?.name} {vehicle.model} {vehicle.version}</p>
                  <p className="text-sm text-mist" data-id="element-1349" data-mp="14duc0nzpixbm">Precio de contado {formatARS(vehicle.price)}</p>
                </div>
                <Link to={`/vehiculo/${vehicle.id}`} className="hidden text-sm text-neon hover:text-neon-hi sm:block" data-id="element-1350" data-mp="10csjeq0apjim">Ver detalle</Link>
              </div>

              <div className="card space-y-6 p-5" data-id="element-1351" data-mp="qlxmmrpfb1ix">
                <div data-id="element-1352" data-mp="qmpkk96htbcd">
                  <div className="flex items-baseline justify-between gap-3" data-id="element-1353" data-mp="qm7gwrhi3uuf">
                    <label htmlFor="sim-down" className="text-sm font-semibold" data-id="element-1354" data-mp="lo5xu6k2rviy">Anticipo</label>
                    <span className="text-xs text-mist" data-id="element-1355" data-mp="gmgk2j5gx3r6">Mínimo {formatARS(bounds.min)} ({ctx.offer.minDownPct}%{ctx.offer.maxFinanceAmount ? ` · tope financiable ${formatARS(ctx.offer.maxFinanceAmount)}` : ''})</span>
                  </div>
                  <input id="sim-down-range" type="range" min={bounds.min} max={bounds.max} step={100000} value={down} onChange={e => {
                setDown(Number(e.target.value));
                setDownInput(e.target.value);
              }} className="mt-3 w-full accent-[#1FA8FF]" aria-label="Anticipo" data-id="element-1356" data-mp="2obulq6pc7tkg" />
                  <div className="mt-3 flex items-center gap-3" data-id="element-1357" data-mp="29l58kysbg4dy">
                    <span className="text-mist" data-id="element-1358" data-mp="2ejf5pexlpf27">$</span>
                    <input id="sim-down" className="input font-semibold" inputMode="numeric" value={Number(downInput || 0).toLocaleString('es-AR')} onChange={e => setDownInput(e.target.value.replace(/\D/g, ''))} onBlur={e => commitDown(e.target.value)} onKeyDown={e => e.key === 'Enter' && commitDown((e.target as HTMLInputElement).value)} data-id="element-1359" data-mp="2ehm1e6agn6xd" />
                  </div>
                </div>

                <fieldset data-id="element-1360" data-mp="t89lbosdt5rt">
                  <legend className="text-sm font-semibold" data-id="element-1361" data-mp="t5do62ocyl4f">Plazo</legend>
                  <div className="mt-3 flex flex-wrap gap-2" data-id="element-1362" data-mp="o6u09wq7ez9f">
                    {ctx.offer.terms.map(t => <label key={t} className={`cursor-pointer rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors duration-150 ${term === t ? 'border-neon bg-neon/10 text-white' : 'border-line text-mist hover:text-white'}`} data-id="element-1363" data-mp="133tieeiomai5">
                        <input type="radio" name="term" className="sr-only" checked={term === t} onChange={() => setTerm(t)} data-id="element-1364" data-mp="17vup7kx2z90s" />
                        {t} meses
                      </label>)}
                  </div>
                  <p className="mt-2 text-xs text-mist" data-id="element-1365" data-mp="1gym2lq9ioev2">Sólo se muestran los plazos habilitados para esta unidad.</p>
                </fieldset>
              </div>

              {sim && <div className="card p-5" data-id="element-1366" data-mp="25gmgg9b9lx8o">
                  <button onClick={() => setShowTable(s => !s)} className="flex w-full items-center justify-between text-sm font-semibold" aria-expanded={showTable} data-id="element-1367" data-mp="25fl3fzaov0ob">
                    Cuadro de amortización estimado
                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${showTable ? 'rotate-180' : ''}`} aria-hidden data-id="element-1368" data-mp="1hsmq3ahv88um" />
                  </button>
                  <AnimatePresence initial={false} data-id="element-1369" data-mp="1ml660e40zg2l">
                    {showTable && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} className="overflow-hidden" data-id="element-1370" data-mp="17w06sq56tpup">
                        <div className="pt-4" data-id="element-1371" data-mp="12v2nga8fyprm"><AmortizationTable rows={sim.schedule} data-id="element-1372" data-mp="12vc6fdv2jwnt" /></div>
                      </motion.div>}
                  </AnimatePresence>
                </div>}
            </>}
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1373" data-mp="3uyszwy8piahr">
          <div className="card overflow-hidden" aria-live="polite" data-id="element-1374" data-mp="3gy8k8ay38ush">
            {vehicle && ctx ? sim ? <>
                  <div className="border-b border-line bg-panel p-5" data-id="element-1375" data-mp="3l14unedh5klc">
                    <p className="text-xs text-mist" data-id="element-1376" data-mp="ckju8lywrztu">{ctx.offer.amortization === 'aleman' ? 'Primera cuota estimada' : 'Cuota mensual estimada'}</p>
                    <p className="font-display text-4xl font-black tracking-tight text-neon" data-id="element-1377" data-mp="3qx1z5twtepe">{formatARS(sim.firstInstallment)}</p>
                    <p className="mt-1 text-sm text-mist" data-id="element-1378" data-mp="7rqqa0b9kvit">
                      {sim.term} cuotas{ctx.offer.amortization === 'aleman' ? ` · última ${formatARS(sim.lastInstallment)}` : ' fijas'}
                    </p>
                    <div className="mt-3" data-id="element-1379" data-mp="109mmenr65b2l"><OfferStatus offer={ctx.offer} source={ctx.source} data-id="element-1380" data-mp="10a4fxcmg5b6t" /></div>
                  </div>
                  <dl className="divide-y divide-line px-5 text-sm" data-id="element-1381" data-mp="2kxf5losmvd3k">
                    {[['Precio del vehículo', formatARS(sim.price)], ['Anticipo', formatARS(sim.down)], ['Capital financiado', formatARS(sim.principal)], ['TNA', formatPct(sim.tna)], ['CFT', ctx.offer.cft === null ? 'No informado por la entidad' : formatPct(ctx.offer.cft)], ['Sistema', amortizationLabel(ctx.offer.amortization)], ['Cantidad de cuotas', String(sim.term)], ['Intereses totales', formatARS(sim.totalInterest)], ['Seguros', ctx.offer.monthlyInsurance === null ? 'No informado' : formatARS(sim.insuranceTotal)], ['Gastos de otorgamiento', ctx.offer.adminFees === null ? 'No informado' : formatARS(sim.adminFees)], ['Costo total estimado', formatARS(sim.totalCost)]].map(([k, v]) => <div key={k} className="flex justify-between gap-4 py-2.5" data-id="element-1382" data-mp="3ugnbionvnwmb">
                        <dt className="text-mist" data-id="element-1383" data-mp="3uiqd85ye7e6v">{k}</dt>
                        <dd className={`text-right ${v.startsWith('No informado') ? 'italic text-mist/70' : 'font-medium'} ${k === 'Costo total estimado' ? 'font-bold text-white' : ''}`} data-id="element-1384" data-mp="3bxbn0zotd1t">{v}</dd>
                      </div>)}
                  </dl>
                  {isOfferPublishable(ctx.offer) && ctx.offer.promo && <p className="mx-5 mb-3 rounded-lg bg-neon/10 px-3 py-2 text-xs text-neon" data-id="element-1385" data-mp="3bo31cofykg7u">Promoción vigente: {ctx.offer.promo}</p>}
                  <div className="space-y-2 p-5 pt-2" data-id="element-1386" data-mp="3lh0ml2cc4ja5">
                    <button onClick={() => setAskOpen(true)} className="btn btn-primary w-full" data-id="element-1387" data-mp="2o136zsv3e2ed">Solicitar asesoramiento con esta simulación</button>
                    <Link to="/chequeo" className="btn btn-ghost w-full" data-id="element-1388" data-mp="2symtth5uvnhx">Chequeo crediticio</Link>
                  </div>
                  <p className="flex gap-2 border-t border-line p-5 text-[11px] leading-relaxed text-mist" data-id="element-1389" data-mp="1a2pfhf5uoglm">
                    <InfoIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden data-id="element-1390" data-mp="161dmcznrpd8o" />
                    {ctx.offer.verified ? `Condiciones verificadas por VCARS, vigentes hasta el ${formatDate(ctx.offer.validUntil)}. Sujeto a aprobación crediticia de ${ctx.offer.lender}. El costo total incluye sólo los cargos informados.` : 'Cálculo matemático referencial con las condiciones cargadas para esta unidad. No constituye una oferta de crédito. La TNA no incluye seguros, gastos ni impuestos; el costo total incluye sólo los cargos informados.'}
                  </p>
                </> : <div className="p-6 text-sm text-mist" data-id="element-1391" data-mp="8fpsomip590u">Esta unidad no tiene una TNA informada todavía. Consultá con un asesor las alternativas de financiación.</div> : <div className="p-6" data-id="element-1392" data-mp="23icnvwa5nn8v">
                <p className="font-display text-lg font-bold" data-id="element-1393" data-mp="23je9uvsplu5d">Tu simulación aparecerá acá</p>
                <p className="mt-1 text-sm text-mist" data-id="element-1394" data-mp="28h5z1i1yz25v">Precio, anticipo, capital, tasa, cuotas y costo total estimado.</p>
              </div>}
          </div>
        </aside>
      </div>

      <Modal open={askOpen} onClose={() => setAskOpen(false)} title="Solicitar asesoramiento" data-id="element-1395" data-mp="1yqbgoorugmpp">
        {vehicle && ctx && sim && <InquiryForm channel="simulador" vehicle={vehicle} simulation={{
        sim,
        ctx
      }} idPrefix="sim" data-id="element-1396" data-mp="1ku7pz5g6iwhz" />}
      </Modal>
    </div>;
}